import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, ResumeTrack, TrackEditModal } from '../../../components';
import { Change, Track, Project } from '../../../models';
import { useAppSelector } from '../../../redux';
import { Table, Badge } from 'flowbite-react';
import moment from 'moment';
import { clientPath } from '../../../utils/clientPath';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../../utils/openChangeInNewBrowserTab';

const condensedTdTheme = {
    "base": "group-first/body:group-first/row:first:rounded-tl-lg group-first/body:group-first/row:last:rounded-tr-lg group-last/body:group-last/row:first:rounded-bl-lg group-last/body:group-last/row:last:rounded-br-lg px-1 py-1"
};

interface TracksProps {
    tracks: Track[];
    shortVersion?: boolean;
    onOpenChange?: (change: Change) => void;
}

function Tracks(props: TracksProps) {
    const { tracks, shortVersion, onOpenChange } = props;

    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const [editingId, setEditingId] = useState<string | undefined>(undefined);

    const { setting } = useAppSelector(state => state.settings);
    const { user } = useAppSelector(state => state.auth);
    const mileageEnabled = !!setting?.mileage?.enabled;

    const trackAuthorId = (track: Track) =>
        typeof track.author === 'string' ? track.author : track.author?._id;

    const canEditTrack = (track: Track) =>
        !!user?._id && trackAuthorId(track) === user._id;

    const editingTrack = editingId
        ? tracks.find((track: Track) => track._id === editingId)
        : undefined;

    const tracksByDay = tracks?.reduce((acc: AnyObj, track: Track) => {
        const day = track.startAt.format('YYYY-MM-DD');
        if (!(day in acc))
            acc[day] = { tracks: [], total: 0 };
        acc[day].tracks.push(track);
        acc[day].total += track.seconds();

        return acc;
    }, {});

    const stopRowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const displayedRow = (track: Track) => (
        <Table.Row
            key={track?._id}
            className={`bg-white dark:border-gray-700 dark:bg-gray-800 ${!shortVersion ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}`}
            onClick={!shortVersion ? () => setEditingId(track._id) : undefined}
        >
            <Table.Cell
                theme={condensedTdTheme}
                className={`whitespace-nowrap font-medium
                    ${!shortVersion ? 'text-gray-900 dark:text-white' : 'text-white dark:text-gray-900 bg-gray-900 !rounded-none'}
                    ${editingId === track._id ? 'bg-blue-50 dark:bg-blue-950/40' : ''}`}
            >
                <div className="flex flex-col md:flex-row space-y-8 md:space-y-0 justify-between">
                    <div className="flex flex-row space-x-2 items-center">
                        <div className="flex-1 flex-col space-y-4 md:space-y-0">
                            <span className="bg-transparent text-center md:text-left text-md font-medium w-full md:w-[24rem] lg:w-auto truncate">
                                {track.title}
                            </span>
                            <div className="flex flex-col items-center md:items-start lg:flex-row space-x-0 lg:space-x-2">
                                {!shortVersion && track.author && trackAuthorId(track) !== user?._id && (
                                    <Badge color="gray">
                                        <div className="flex flex-row space-x-1.5 items-center">
                                            <Icon type="user" size={3} />
                                            <div className="text-xs font-normal truncate">
                                                {typeof track.author === 'string'
                                                    ? track.author
                                                    : track.author.fullname()}
                                            </div>
                                        </div>
                                    </Badge>
                                )}
                                <Badge
                                    color="success"
                                    onClick={(e) => {
                                        stopRowClick(e);
                                        navigate(Project.path(track.project));
                                    }}
                                    className="cursor-pointer"
                                >
                                    <div className="flex flex-row space-x-1.5 items-center">
                                        <Icon type="project" className="" size={3} />
                                        <div className="text-xs font-normal w-full md:w-64 truncate">{track.project?.name}</div>
                                    </div>
                                </Badge>
                                {!shortVersion && track.client && (
                                    <Badge
                                        color="warning"
                                        onClick={(e) => {
                                            stopRowClick(e);
                                            if (track.client?._id)
                                                navigate(clientPath(track.client._id));
                                        }}
                                        className={track.client?._id ? 'cursor-pointer' : ''}
                                    >
                                        <div className="flex flex-row space-x-1.5 items-center">
                                            <Icon type="user" className="" size={3} />
                                            <div className="text-xs font-normal w-full md:w-64 truncate">{track.client.name()}</div>
                                        </div>
                                    </Badge>
                                )}
                                {!shortVersion && track.change && (
                                    <Badge
                                        color={track.change.color()}
                                        onClick={(e) => {
                                            stopRowClick(e);
                                            if (onOpenChange)
                                                return onOpenChange(track.change as Change);
                                            navigate(Project.path(track.project, 'change', track.change?._id));
                                        }}
                                        onMouseDown={preventMiddleClickAutoscroll}
                                        onAuxClick={(event) => handleChangeMiddleClick(
                                            event,
                                            track.change as Change,
                                            track.project,
                                        )}
                                        className="cursor-pointer"
                                    >
                                        <div className="flex flex-row space-x-1.5 items-center">
                                            <Icon type={track.change.iconName()} className="" size={3} />
                                            <div className="text-xs font-normal w-full md:w-64 truncate">
                                                {track.change.slugName()}&nbsp;|&nbsp;
                                                {track.change.localizedTitle(i18n.language)}
                                            </div>
                                        </div>
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6 items-center md:items-end">
                        {!shortVersion && (
                        <>
                            {mileageEnabled && (
                                <div className="w-20 flex justify-center md:justify-end shrink-0">
                                    {track.km != null && track.km > 0 && (
                                        <Badge color="indigo">
                                            <div className="flex flex-row space-x-1 items-center">
                                                <span className="text-xs font-bold whitespace-nowrap">
                                                    {track.km} {t('tracks.km_unit')}
                                                </span>
                                            </div>
                                        </Badge>
                                    )}
                                </div>
                            )}
                            <div className="flex flex-col text-xl">
                                <div className="flex flex-row space-x-4 justify-center items-center font-bold">
                                    <div className="flex flex-col items-center">
                                        <span className="bg-gray-50 dark:bg-gray-900 border-gray-50 text-xs text-center font-medium">
                                            {track.startAt.format('LL')}
                                        </span>
                                        <span className="block font-bold text-sm">
                                            {track.startAt.format('HH:mm')}
                                        </span>
                                    </div>
                                    {track.stopAt && (
                                        <div className="flex flex-col items-center">
                                            <span className="bg-gray-50 dark:bg-gray-900 border-gray-50 text-xs text-center font-medium">
                                                {track.stopAt.format('LL')}
                                            </span>
                                            <span className="block font-bold text-sm">
                                                {track.stopAt.format('HH:mm')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="text-2xl font-bold w-32 text-center md:text-right">
                                {!track.isRunning() && track.formattedDuration()}
                            </div>
                        </>
                        )}
                        <div
                            className="text-2xl font-bold cursor-pointer"
                            onClick={stopRowClick}
                        >
                            <ResumeTrack track={track} className="w-12" />
                        </div>
                    </div>
                </div>
            </Table.Cell>
        </Table.Row>
    );

    return (
        <div className="Tracks">
            {Object.keys(tracksByDay).sort((a: string, b: string) => b.localeCompare(a)).map((day: string) => (
                <Table key={day} striped hoverable className="mb-4">
                    <Table.Body className={!shortVersion ? "divide-y" : ""}>
                        {!shortVersion && (
                        <Table.Row>
                            <Table.Cell theme={condensedTdTheme} className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                <h3 className="flex justify-between items-center text-xl font-bold my-1">
                                    <span className="flex items-center gap-x-2">
                                        <Icon type="date" />
                                        {moment(day).format('LL')}
                                    </span>
                                    <span className="flex items-center gap-x-2">
                                        <Icon type="timer" />
                                        {Track.format(tracksByDay[day].total)}
                                    </span>
                                </h3>
                            </Table.Cell>
                        </Table.Row>
                        )}
                        {tracksByDay[day]?.tracks
                            .sort((a: Track, b: Track) => b.startAt.unix() - a.startAt.unix())
                            .map((track: Track) => displayedRow(track))}
                    </Table.Body>
                </Table>
            ))}

            {editingTrack && (
                <TrackEditModal
                    show
                    track={editingTrack}
                    readOnly={!canEditTrack(editingTrack)}
                    onClose={() => setEditingId(undefined)}
                />
            )}
        </div>
    );
}

export default Tracks;
